import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material'
import { useToasts } from 'react-toast-notifications'
import { PatientHistoryType } from 'src/graphql.generated'
import { PATIENT_HISTORY_TYPE_NAMES, PatientHistoryFormType } from 'src/participantHistory/constants'
import { usePatientHistoryFormTypes } from '../../../api/usePatientHistoryFormTypes'
import { EntryAddedProvider } from '../../../api/useSubmitPatientHistory'
import { CallNotesRequest, LinkedCallProvider, useCallNotesRequest } from '../../../dashboard/callNotesRequest'
import { usePanelPatientUid } from '../../../dashboard/panelContext'
import { SELECT_MENU_PROPS } from '../../fieldPrimitives'
import BurnoutFields from './fields/BurnoutFields'
import CheckInCallFields from './fields/CheckInCallFields'
import DischargeSummaryNoteFields from './fields/DischargeSummaryNoteFields'
import FinancialEligibilityHealthPlanFields from './fields/FinancialEligibilityHealthPlanFields'
import GAD7Fields from './fields/GAD7Fields'
import IntakeNoteFields from './fields/IntakeNoteFields'
import IntakeNoteNutritionFields from './fields/IntakeNoteNutritionFields'
import IntakeNotePsychiatryFields from './fields/IntakeNotePsychiatryFields'
import MissedIntakeCallFields from './fields/MissedIntakeCallFields'
import NoteFields from './fields/NoteFields'
import PHQ9Fields from './fields/PHQ9Fields'
import TreatmentPlanFields from './fields/TreatmentPlanFields'
import WHO5Fields from './fields/WHO5Fields'
import WPAIFields from './fields/WPAIFields'
import {
  AdvancedFollowUpNoteFields,
  IntakeNoteCoachingFields,
  IntakeNoteComprehensiveFields,
  MonthlySummaryNoteFields,
  TreatmentPlanComprehensiveFields,
  TreatmentPlanComprehensiveUpdateFields,
} from './SpecForms'
import { ADVANCED_NOTE_KIND_LABELS, CLINICAL_CONSULTATION_NOTE, INTAKE_NOTE_NUTRITION } from './advancedNoteKinds'
import { SUPPORTED_TYPES } from './supportedTypes'

const NOTE_LIKE_TYPES: Partial<Record<string, PatientHistoryType>> = {
  NOTE: PatientHistoryType.Note,
  PHONE_CALL: PatientHistoryType.PhoneCall,
  SMS: PatientHistoryType.Sms,
  LAB_ORDER_NOTE: PatientHistoryType.LabOrderNote,
  FINANCIAL_ELIGIBILITY_CONFIRMED: PatientHistoryType.FinancialEligibilityConfirmed,
  [CLINICAL_CONSULTATION_NOTE]: PatientHistoryType.Note,
}

const DEDICATED_FIELDS: Partial<Record<string, React.ComponentType<{ uid: string }>>> = {
  BURNOUT: BurnoutFields,
  CHECK_IN_CALL: CheckInCallFields,
  DISCHARGE_SUMMARY_NOTE: DischargeSummaryNoteFields,
  GAD7: GAD7Fields,
  INTAKE_NOTE: IntakeNoteFields,
  INTAKE_NOTE_PSYCHIATRY: IntakeNotePsychiatryFields,
  [INTAKE_NOTE_NUTRITION]: IntakeNoteNutritionFields,
  MISSED_INTAKE_CALL: MissedIntakeCallFields,
  PHQ9: PHQ9Fields,
  TREATMENT_PLAN: TreatmentPlanFields,
  WPAI: WPAIFields,
  WHO5: WHO5Fields,
  INTAKE_NOTE_COACHING: IntakeNoteCoachingFields,
  ADVANCED_FOLLOW_UP_NOTE: AdvancedFollowUpNoteFields,
  MONTHLY_SUMMARY_NOTE: MonthlySummaryNoteFields,
  TREATMENT_PLAN_COMPREHENSIVE: TreatmentPlanComprehensiveFields,
  TREATMENT_PLAN_COMPREHENSIVE_UPDATE: TreatmentPlanComprehensiveUpdateFields,
  INTAKE_NOTE_COMPREHENSIVE: IntakeNoteComprehensiveFields,
  FINANCIAL_ELIGIBILITY_HEALTH_PLAN: FinancialEligibilityHealthPlanFields,
}

const labelFor = (type: string) =>
  ADVANCED_NOTE_KIND_LABELS[type] ?? PATIENT_HISTORY_TYPE_NAMES[type as PatientHistoryFormType] ?? type

// A type with no field-set here is silently skipped — extend `DEDICATED_FIELDS`/`NOTE_LIKE_TYPES` to add one.
const EventLogger = () => {
  const uid = usePanelPatientUid()
  const availableTypes = usePatientHistoryFormTypes(uid)
  const request = useCallNotesRequest()
  const [selected, setSelected] = useState('')
  const { addToast } = useToasts()
  const [linkedCall, setLinkedCall] = useState<CallNotesRequest | null>(null)
  const loggerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (request && request.uid === uid) {
      setSelected(PatientHistoryType.CheckInCall)
      setLinkedCall(request)
      loggerRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' })
    }
  }, [request, uid])

  const onAdded = useCallback(() => {
    addToast(`${labelFor(selected)} added.`, { appearance: 'success' })
    setSelected('')
    setLinkedCall(null)
  }, [addToast, selected])

  const patientHistoryOptions = availableTypes.flatMap((type) => {
    if (type === 'FINANCIAL_ELIGIBILITY_HEALTH_PLAN' || !SUPPORTED_TYPES.includes(type)) return []

    if (type === 'NOTE') return [type, CLINICAL_CONSULTATION_NOTE]

    return type === 'FINANCIAL_ELIGIBILITY_CONFIRMED' ? [type, 'FINANCIAL_ELIGIBILITY_HEALTH_PLAN'] : [type]
  })
  const options = [...patientHistoryOptions, INTAKE_NOTE_NUTRITION]

  const noteType = NOTE_LIKE_TYPES[selected]
  const DedicatedFields = DEDICATED_FIELDS[selected]

  return (
    <Box ref={loggerRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <FormControl size="small" fullWidth>
        <InputLabel id="event-logger-type-label">Type</InputLabel>
        <Select
          labelId="event-logger-type-label"
          label="Type"
          value={selected}
          MenuProps={SELECT_MENU_PROPS}
          onChange={(event) => {
            setLinkedCall(null)
            setSelected(event.target.value)
          }}
        >
          {options.map((type) => (
            <MenuItem key={type} value={type}>
              {labelFor(type)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {/* Keyed by patient too: the panel outlives a patient switch, and a draft must not follow it. */}
      <EntryAddedProvider value={onAdded}>
        <LinkedCallProvider value={linkedCall?.uid === uid ? linkedCall.callID : ''}>
          {noteType && (
            <NoteFields
              key={`${selected}:${uid}`}
              uid={uid}
              type={noteType}
              advancedNoteKind={ADVANCED_NOTE_KIND_LABELS[selected] ? selected : undefined}
            />
          )}
          {DedicatedFields && <DedicatedFields key={`${selected}:${uid}:${linkedCall?.callID ?? ''}`} uid={uid} />}
        </LinkedCallProvider>
      </EntryAddedProvider>
    </Box>
  )
}

export default EventLogger
